import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmActionModal } from '@/components/ui/ConfirmActionModal';
import { SessionCallResizableSplit } from '@/components/session-call/SessionCallResizableSplit';
import { SessionCallProntuarioPanel } from '@/pages/professional/sessao-chamada/components';
import { SessionEndTransition } from '@/pages/professional/sessao-chamada/components/prontuario/session-end-transition';
import { SessionEndProntuarioReview } from '@/pages/professional/sessao-chamada/components/prontuario/session-end-review/SessionEndProntuarioReview';
import { PRONTUARIO_BG_CLASS } from '@/pages/professional/sessao-chamada/constants/prontuarioTheme';
import type { SessionCallLayoutOutletContext } from '@/constants/sessionCall';
import {
	isProfessionalTutorialDemoAccess,
	isTutorialDemoPlayTransitionEntry,
	TUTORIAL_DEMONSTRACAO_BACKDROP_BG,
	createProfessionalTutorialHomeReturnState,
} from '@/constants/professionalTutorialDemo';
import { cn } from '@/lib/utils';
import { getProfessionalData } from '@/utils/cookies';
import {
	DailyCallDemonstracaoMock,
	type ProntuarioDemoBridge,
} from './DailyCallDemonstracaoMock';
import { TutorialDemonstracaoEntryBlackCurtain } from './TutorialDemonstracaoEntryBlackCurtain';
import { TutorialDemonstracaoPrejoinScreen } from './TutorialDemonstracaoPrejoinScreen';
import {
	TutorialDemonstracaoCallCoachBalloon,
	TUTORIAL_DEMO_END_CONFIRM_MODAL_SELECTOR,
} from './TutorialDemonstracaoCallCoachBalloon';
import { TutorialDemonstracaoTransitionProcessingHint } from './TutorialDemonstracaoTransitionProcessingHint';
import { useTutorialDemonstracaoMedia } from './useTutorialDemonstracaoMedia';
import {
	seedTutorialDemonstracaoPostEndQueryCache,
	TUTORIAL_DEMO_SESSION_ID,
} from './seedTutorialDemonstracaoPostEndQueryCache';
import { seedTutorialDemonstracaoQueryCache } from './seedTutorialDemonstracaoQueryCache';
import {
	buildTutorialDemonstracaoEndedSession,
	buildTutorialDemonstracaoPatient,
	buildTutorialDemonstracaoSession,
	TUTORIAL_DEMO_MOCK_REMOTE_PARTICIPANT_NAME,
	TUTORIAL_DEMO_MOCK_REMOTE_PARTICIPANT_TILE_LABEL,
} from './tutorialDemonstracaoMock';

/** Tempo de animação antes de simular is_done na transição demo. */
const DEMO_TRANSITION_PROCESSING_MS = 8_000;

type DemoPhase = 'lobby' | 'call' | 'transition' | 'review';

const ProfessionalTutorialDemonstracaoPage = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const callContainerRef = useRef<HTMLDivElement>(null);
	const prontuarioDemoBridgeRef = useRef<ProntuarioDemoBridge>({
		onPatientCardOpen: () => undefined,
		onSessionsOpen: () => undefined,
		onMinimize: () => undefined,
		onViewChange: () => undefined,
		onEndConfirmComplete: () => undefined,
	});
	const [callMountNode, setCallMountNode] = useState<HTMLElement | null>(
		null,
	);
	const { setVideoCallHeaderConfig } =
		useOutletContext<SessionCallLayoutOutletContext>();

	const [demoPhase, setDemoPhase] = useState<DemoPhase>('lobby');
	const [isProntuarioOpen, setIsProntuarioOpen] = useState(false);
	const [isDemoReady, setIsDemoReady] = useState(false);
	const [isEndConfirmOpen, setIsEndConfirmOpen] = useState(false);
	const [isTutorialEndConfirmCoachOpen, setIsTutorialEndConfirmCoachOpen] =
		useState(false);
	const [reviewSession, setReviewSession] = useState<Sessions.Session | null>(
		null,
	);
	const [showEntryBlackCurtain, setShowEntryBlackCurtain] = useState(() =>
		isTutorialDemoPlayTransitionEntry(location.state),
	);

	const hasValidAccess = isProfessionalTutorialDemoAccess(location.state);
	const professional = getProfessionalData();
	const professionalId = professional?.id;

	const demoSession = useMemo(() => {
		if (!professionalId) return null;

		const currentProfessional = getProfessionalData();
		if (!currentProfessional) return null;

		const patient = buildTutorialDemonstracaoPatient(professionalId);
		return buildTutorialDemonstracaoSession({
			professional: currentProfessional,
			patient,
		});
	}, [professionalId]);

	const [demoCallEnteredAtMs, setDemoCallEnteredAtMs] = useState<
		number | null
	>(null);

	const mediaEnabled = demoPhase === 'lobby' || demoPhase === 'call';
	const media = useTutorialDemonstracaoMedia(mediaEnabled);

	const handleJoinDemonstration = useCallback(() => {
		setDemoCallEnteredAtMs(Date.now());
		setDemoPhase('call');
	}, []);

	const participantDisplayName = useMemo(() => {
		if (!professional) return 'Profissional';
		return [professional.first_name, professional.last_name]
			.filter(Boolean)
			.join(' ')
			.trim();
	}, [professional]);

	const returnToTutorialModal = useCallback(() => {
		navigate('/profissional/home', {
			replace: true,
			state: createProfessionalTutorialHomeReturnState(),
		});
	}, [navigate]);

	const handleRequestEndDemonstration = useCallback(
		(options?: { withTutorialCoach?: boolean }) => {
			setIsTutorialEndConfirmCoachOpen(!!options?.withTutorialCoach);
			setIsEndConfirmOpen(true);
		},
		[],
	);

	const handleCloseEndConfirm = useCallback(() => {
		setIsEndConfirmOpen(false);
		setIsTutorialEndConfirmCoachOpen(false);
	}, []);

	const handleConfirmEndDemonstration = useCallback(() => {
		if (!professional) return;

		prontuarioDemoBridgeRef.current.onEndConfirmComplete();
		setIsEndConfirmOpen(false);
		setIsTutorialEndConfirmCoachOpen(false);
		seedTutorialDemonstracaoPostEndQueryCache(queryClient, professional);

		const patient = buildTutorialDemonstracaoPatient(professional.id);
		setReviewSession(
			buildTutorialDemonstracaoEndedSession({ professional, patient }),
		);
		setIsProntuarioOpen(false);
		setDemoPhase('transition');
	}, [professional, queryClient]);

	const handleTransitionComplete = useCallback(() => {
		setDemoPhase('review');
	}, []);

	useLayoutEffect(() => {
		if (!hasValidAccess) {
			navigate('/profissional/home', { replace: true });
		}
	}, [hasValidAccess, navigate]);

	useLayoutEffect(() => {
		if (!hasValidAccess || !professional?.id) return;

		let cancelled = false;

		const prepareDemo = () => {
			seedTutorialDemonstracaoQueryCache(queryClient, professional.id);
			if (!cancelled) setIsDemoReady(true);
		};

		prepareDemo();

		return () => {
			cancelled = true;
		};
	}, [hasValidAccess, professional?.id, queryClient]);

	useLayoutEffect(() => {
		if (!isDemoReady || demoPhase !== 'call') {
			setCallMountNode(null);
			return;
		}

		setCallMountNode(callContainerRef.current);
	}, [demoPhase, isDemoReady]);

	useLayoutEffect(() => {
		if (!hasValidAccess) return;

		const headerPhases: DemoPhase[] = [
			'lobby',
			'call',
			'transition',
			'review',
		];
		if (!headerPhases.includes(demoPhase)) {
			return;
		}

		const showMeetingTimer =
			demoPhase === 'call' && demoCallEnteredAtMs !== null;

		setVideoCallHeaderConfig({
			onBack: returnToTutorialModal,
			onLogoClick: returnToTutorialModal,
			onToggleLayout: () => undefined,
			activeSpeakerMode: true,
			controlsEnabled: false,
			showLayoutToggle: false,
			hideBackButton:
				demoPhase === 'transition' || demoPhase === 'review',
			meetingScheduledStartMs: showMeetingTimer
				? demoCallEnteredAtMs
				: null,
		});

		return () => setVideoCallHeaderConfig(null);
	}, [
		demoCallEnteredAtMs,
		demoPhase,
		returnToTutorialModal,
		hasValidAccess,
		setVideoCallHeaderConfig,
	]);

	const handleToggleProntuario = useCallback(() => {
		setIsProntuarioOpen((current) => !current);
	}, []);

	if (!hasValidAccess || !professional) {
		return null;
	}

	const pageShell = (
		<div
			className={cn(
				'relative flex h-full min-h-0 w-full flex-col overflow-hidden',
				demoPhase === 'lobby' ? undefined : PRONTUARIO_BG_CLASS,
			)}
			style={
				demoPhase === 'lobby'
					? { backgroundColor: TUTORIAL_DEMONSTRACAO_BACKDROP_BG }
					: undefined
			}
		>
			{isDemoReady && demoSession && demoPhase === 'lobby' ? (
				<TutorialDemonstracaoPrejoinScreen
					participantDisplayName={participantDisplayName}
					media={media}
					onJoin={handleJoinDemonstration}
				/>
			) : null}

			{isDemoReady && demoSession && demoPhase === 'call' ? (
				<>
					<SessionCallResizableSplit
						callContainerRef={callContainerRef}
						prontuarioOpen={isProntuarioOpen}
						prontuarioEnabled
						showProntuarioBadge={!isProntuarioOpen}
						onOpenProntuario={() => setIsProntuarioOpen(true)}
						prontuarioBadgeHint="hidden"
						prontuarioPane={
							<SessionCallProntuarioPanel
								session={demoSession}
								className="h-full w-full"
								demoMode
								onMinimize={() => setIsProntuarioOpen(false)}
								onDemoPatientCardOpen={() =>
									prontuarioDemoBridgeRef.current.onPatientCardOpen()
								}
								onDemoSessionsOpen={() =>
									prontuarioDemoBridgeRef.current.onSessionsOpen()
								}
								onDemoMinimize={() =>
									prontuarioDemoBridgeRef.current.onMinimize()
								}
								onDemoViewChange={(view) =>
									prontuarioDemoBridgeRef.current.onViewChange(
										view,
									)
								}
							/>
						}
					/>

					{callMountNode
						? createPortal(
								<DailyCallDemonstracaoMock
									participantDisplayName={
										participantDisplayName
									}
									mockPatientDisplayName={
										TUTORIAL_DEMO_MOCK_REMOTE_PARTICIPANT_NAME
									}
									mockPatientTileLabel={
										TUTORIAL_DEMO_MOCK_REMOTE_PARTICIPANT_TILE_LABEL
									}
									isProntuarioOpen={isProntuarioOpen}
									onToggleProntuario={handleToggleProntuario}
									onEndDemonstration={
										handleRequestEndDemonstration
									}
									isEndConfirmOpen={isEndConfirmOpen}
									stream={media.stream}
									screenStream={media.screenStream}
									isScreenSharing={media.isScreenSharing}
									camOn={media.camOn}
									micOn={media.micOn}
									hasCamera={media.cameras.length > 0}
									hasMicrophone={media.microphones.length > 0}
									onToggleCamera={media.toggleCamera}
									onToggleMicrophone={media.toggleMicrophone}
									onStartScreenShare={media.startScreenShare}
									onStopScreenShare={media.stopScreenShare}
									registerProntuarioDemoBridge={(bridge) => {
										prontuarioDemoBridgeRef.current =
											bridge;
									}}
								/>,
								callMountNode,
						  )
						: null}
				</>
			) : null}

			{isDemoReady && demoSession && demoPhase === 'transition' ? (
				<>
					<SessionEndTransition
						sessionId={TUTORIAL_DEMO_SESSION_ID}
						onComplete={handleTransitionComplete}
						demoSimulateProcessingDoneMs={
							DEMO_TRANSITION_PROCESSING_MS
						}
					/>
					<TutorialDemonstracaoTransitionProcessingHint />
				</>
			) : null}

			{isDemoReady &&
			demoSession &&
			demoPhase === 'review' &&
			reviewSession ? (
				<SessionEndProntuarioReview
					session={reviewSession}
					onGoHome={returnToTutorialModal}
					onViewProntuario={() => returnToTutorialModal()}
					demoMode
				/>
			) : null}

			<ConfirmActionModal
				isOpen={isEndConfirmOpen}
				onClose={handleCloseEndConfirm}
				onConfirm={handleConfirmEndDemonstration}
				title="Encerrar reunião"
				description="Você tem certeza que deseja encerrar a reunião?"
				confirmButtonText="Encerrar reunião"
				tutorialDemoTarget="end-confirm-modal"
			/>
			{isEndConfirmOpen && isTutorialEndConfirmCoachOpen ? (
				<TutorialDemonstracaoCallCoachBalloon
					targetSelector={TUTORIAL_DEMO_END_CONFIRM_MODAL_SELECTOR}
					bodyId="demo-call-coach-end-confirm"
					balloonWidthPx={360}
					placement="above"
					compact
					allowTargetInteraction
					allowHeaderInteraction={false}
					portalZIndex={10030}
					showContinueButton={false}
					onContinue={() => undefined}
				>
					Ao encerrar a sessão, a transcrição e suas anotações serão
					salvas automaticamente. Com base no conteúdo da chamada, a
					IA irá fazer um pré-preenchimento do prontuário.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
		</div>
	);

	if (!hasValidAccess || !professional) {
		return null;
	}

	if ((!isDemoReady || !demoSession) && !showEntryBlackCurtain) {
		return null;
	}

	return (
		<>
			{isDemoReady && demoSession ? (
				pageShell
			) : (
				<div
					className="h-full min-h-0 w-full"
					style={{
						backgroundColor: TUTORIAL_DEMONSTRACAO_BACKDROP_BG,
					}}
				/>
			)}
			{showEntryBlackCurtain ? (
				<TutorialDemonstracaoEntryBlackCurtain
					onComplete={() => setShowEntryBlackCurtain(false)}
				/>
			) : null}
		</>
	);
};

export { ProfessionalTutorialDemonstracaoPage };
